import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from '../firebase';
import { db, auth } from '../firebase';
import { Download, Calendar, Search, Filter, Lock, Loader2, X } from 'lucide-react';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';

const exportToCSV = (data: any[], filename: string) => {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const val = row[header];
      return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(','))
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export default function TrialBalanceView({ orgId, language, onNavigate }: { orgId: string, language: string, onNavigate?: (tab: string, id?: string | null, extra?: any) => void }) {
  const [trialBalance, setTrialBalance] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'both' | 'non-zero' | 'zero'>('both');
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [isClosingModalOpen, setIsClosingModalOpen] = useState(false);
  const [closingDate, setClosingDate] = useState(new Date().toISOString().split('T')[0]);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('app_auth_token');
        if (token) {
          const headers = { Authorization: `Bearer ${token}` };
          const [accountsResponse, ledgerResponse] = await Promise.all([
            fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers }),
            fetch(`/api/organizations/${orgId}/reports/general-ledger`, { headers })
          ]);
          const [localAccounts, ledgerLines] = await Promise.all([
            accountsResponse.json(), ledgerResponse.json()
          ]);
          if (!accountsResponse.ok || !ledgerResponse.ok) throw new Error('Could not load local trial balance data');
          const getCategory = (type: string) => {
            const normalized = (type || '').toLowerCase();
            if (['bank', 'current asset', 'fixed asset', 'inventory', 'prepayment', 'accounts receivable', 'non-current asset'].includes(normalized)) return 'Assets';
            if (['accounts payable', 'current liability', 'liability', 'non-current liability', 'credit card'].includes(normalized)) return 'Liabilities';
            if (normalized === 'equity') return 'Equity';
            if (['sales', 'other income', 'revenue'].includes(normalized)) return 'Income';
            if (['direct cost', 'expense', 'overheads', 'depreciation', 'other expense'].includes(normalized)) return 'Expenses';
            return 'Other';
          };
          const rows: Record<string, any> = {};
          (Array.isArray(localAccounts) ? localAccounts : []).forEach((account: any) => {
            rows[account.id] = { id: account.id, name: account.name, code: account.code || '', type: account.type || 'Other', category: getCategory(account.type), initialBalance: 0, periodDebit: 0, periodCredit: 0, endingBalance: 0 };
          });
          (Array.isArray(ledgerLines) ? ledgerLines : []).forEach((line: any) => {
            const account = rows[line.accountId || line.account_id];
            if (!account) return;
            const date = String(line.date || '').slice(0, 10);
            const debit = Number(line.debit || 0), credit = Number(line.credit || 0);
            if (date < startDate) account.initialBalance += debit - credit;
            else if (date <= endDate) { account.periodDebit += debit; account.periodCredit += credit; }
          });
          const parsed = Object.values(rows).map((row: any) => ({ ...row, endingBalance: row.initialBalance + row.periodDebit - row.periodCredit }));
          setAccounts(Array.isArray(localAccounts) ? localAccounts : []);
          setTrialBalance(parsed);
          return;
        }
        const [accountsSnap, journalsSnap] = await Promise.all([
          getDocs(query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId))),
          getDocs(query(collection(db, 'manual_journals'), where('orgId', '==', orgId), where('status', '==', 'Posted')))
        ]);

        const accs = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
        setAccounts(accs);
        const journals = journalsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);

        const tb: Record<string, { 
          id: string, 
          name: string, 
          code: string, 
          type: string, 
          category: string,
          initialBalance: number,
          periodDebit: number, 
          periodCredit: number,
          endingBalance: number 
        }> = {};
        
        const getCategory = (type: string) => {
          const t = type?.toLowerCase() || '';
          if (['bank', 'current asset', 'fixed asset', 'inventory', 'prepayment', 'accounts receivable', 'non-current asset'].includes(t)) return 'Assets';
          if (['accounts payable', 'current liability', 'liability', 'non-current liability', 'credit card'].includes(t)) return 'Liabilities';
          if (['equity'].includes(t)) return 'Equity';
          if (['sales', 'other income', 'revenue'].includes(t)) return 'Income';
          if (['direct cost', 'expense', 'overheads', 'depreciation', 'other expense'].includes(t)) return 'Expenses';
          return 'Other';
        };

        accs.forEach((acc: any) => {
          tb[acc.id] = { 
            id: acc.id, 
            name: acc.name, 
            code: acc.code || '', 
            type: acc.type || 'Other',
            category: getCategory(acc.type),
            initialBalance: 0,
            periodDebit: 0, 
            periodCredit: 0,
            endingBalance: 0
          };
        });

        const accountByCode: Record<string, any> = {};
        accs.forEach(acc => { if (acc.code) accountByCode[acc.code] = acc; });

        const processAdjustment = (accId: string, accCode: string, debit: number, credit: number, date: string) => {
          const d = Math.max(0, debit);
          const c = Math.max(0, credit);
          
          let targetId = accId;
          const suspenseId = 'suspense_account';

          if (!targetId || !tb[targetId]) {
            if (accCode && accountByCode[accCode]) {
              targetId = accountByCode[accCode].id;
            } else {
              targetId = suspenseId;
            }

            if (!tb[targetId]) {
              tb[targetId] = { 
                id: targetId, 
                name: 'Suspense/Unmapped Account', 
                code: accCode || '999', 
                type: 'Other', 
                category: 'Other', 
                initialBalance: 0, 
                periodDebit: 0, 
                periodCredit: 0, 
                endingBalance: 0 
              };
            }
          }
          
          if (date < startDate) {
            tb[targetId].initialBalance += (d - c);
          } else if (date >= startDate && date <= endDate) {
            tb[targetId].periodDebit += d;
            tb[targetId].periodCredit += c;
          }
        };

        journals.forEach((j: any) => {
          (j.lines || []).forEach((line: any) => {
            processAdjustment(line.accountId, line.accountCode, line.debit || 0, line.credit || 0, j.date);
          });
        });

        const parsed = Object.values(tb).map((data) => {
          const endingBalance = data.initialBalance + data.periodDebit - data.periodCredit;
          return {
            ...data,
            endingBalance
          };
        });

        setTrialBalance(parsed);

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, startDate, endDate]);

  const filtered = trialBalance.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase()) || a.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const isZero = Math.abs(a.initialBalance) < 0.001 && 
                   Math.abs(a.periodDebit) < 0.001 && 
                   Math.abs(a.periodCredit) < 0.001 &&
                   Math.abs(a.endingBalance) < 0.001;
    
    if (visibilityFilter === 'non-zero' && isZero) return false;
    if (visibilityFilter === 'zero' && !isZero) return false;
    
    return matchesSearch;
  });

  const handleYearEndClosing = async () => {
    setIsClosing(true);
    try {
      const localToken = localStorage.getItem('app_auth_token');
      const localRequest = async (path: string, init: RequestInit = {}) => {
        const response = await fetch(path, { ...init, headers: { Authorization: `Bearer ${localToken}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers } });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
        return payload;
      };
      // 1. Identify Retained Earnings account
      let reAccount = accounts.find(a => a.name.toLowerCase().includes('retained earning') || a.code === '3900');
      
      if (!reAccount) {
        notify.confirm("Retained Earnings account not found. Creating it...");
        if (localToken) {
          reAccount = await localRequest(`/api/organizations/${orgId}/chart-of-accounts`, {
            method: 'POST',
            body: JSON.stringify({ code: '3900', name: 'Retained Earnings', type: 'Equity', nature: 'Credit', status: 'active', isSystemAccount: true })
          });
        } else {
          const docRef = await addDoc(collection(db, 'chart_of_accounts'), {
            orgId,
            code: '3900',
            name: 'Retained Earnings',
            type: 'Equity',
            nature: 'Credit',
            status: 'active',
            createdAt: serverTimestamp()
          });
          reAccount = { id: docRef.id, name: 'Retained Earnings', code: '3900' };
        }
      }

      // 2. Filter P&L accounts (Income and Expenses)
      const plAccounts = trialBalance.filter(a => a.category === 'Income' || a.category === 'Expenses');
      
      const journalLines: any[] = [];
      let netProfitLoss = 0;

      plAccounts.forEach(acc => {
        if (Math.abs(acc.endingBalance) < 0.01) return;

        // To zero out a debit balance, we credit it.
        // To zero out a credit balance, we debit it.
        // Balance = Debit - Credit. 
        // If Balance > 0 (Debit), we need Credit = Balance.
        // If Balance < 0 (Credit), we need Debit = abs(Balance).
        
        const debit = acc.endingBalance < 0 ? Math.abs(acc.endingBalance) : 0;
        const credit = acc.endingBalance > 0 ? acc.endingBalance : 0;

        journalLines.push({
          id: crypto.randomUUID(),
          accountId: acc.id,
          accountName: acc.name,
          description: `Year-End Closing ${closingDate}`,
          debit,
          credit
        });

        netProfitLoss -= (debit - credit); // The net effect on retained earnings
      });

      if (journalLines.length === 0) {
        notify.error("No balances found in P&L accounts to close.");
        setIsClosing(false);
        return;
      }

      // 3. Balance the entry with Retained Earnings
      journalLines.push({
        id: crypto.randomUUID(),
        accountId: reAccount.id,
        accountName: reAccount.name,
        description: `Year-End Net Result ${closingDate}`,
        debit: netProfitLoss < 0 ? Math.abs(netProfitLoss) : 0,
        credit: netProfitLoss > 0 ? netProfitLoss : 0
      });

      // 4. Save the manual journal
      if (localToken) {
        const journal = await localRequest(`/api/organizations/${orgId}/manual-journals`, {
          method: 'POST',
          body: JSON.stringify({
            journalNumber: `CLS/${closingDate.split('-')[0]}/${closingDate.split('-')[1]}/0001`,
            date: closingDate,
            description: `Year-End Closing for period through ${closingDate}`,
            status: 'draft',
            lines: journalLines
          })
        });
        await localRequest(`/api/organizations/${orgId}/manual-journals/${journal.id}/post`, { method: 'POST' });
      } else {
        await addDoc(collection(db, 'manual_journals'), {
          orgId,
          journalNumber: `CLS/${closingDate.split('-')[0]}/${closingDate.split('-')[1]}/0001`,
          date: closingDate,
          narration: `Year-End Closing for period through ${closingDate}`,
          status: 'Posted',
          lines: journalLines,
          totalDebit: journalLines.reduce((sum, l) => sum + l.debit, 0),
          totalCredit: journalLines.reduce((sum, l) => sum + l.credit, 0),
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid
        });
      }

      notify.confirm("Year-End Closing completed successfully!");
      setIsClosingModalOpen(false);
    } catch (err) {
      console.error(err);
      notify.error("Failed to perform year-end closing.");
    } finally {
      setIsClosing(false);
    }
  };
  
  const categories = ['Assets', 'Liabilities', 'Equity', 'Income', 'Expenses', 'Other'];
  
  const totals = trialBalance.reduce((acc, a) => ({
    initial: acc.initial + a.initialBalance,
    debit: acc.debit + a.periodDebit,
    credit: acc.credit + a.periodCredit,
    ending: acc.ending + a.endingBalance
  }), { initial: 0, debit: 0, credit: 0, ending: 0 });

  const handleExport = () => {
    const data = filtered.map(a => ({
      Account: a.name,
      Code: a.code,
      Type: a.type,
      'Initial Balance': a.initialBalance,
      Debit: a.periodDebit,
      Credit: a.periodCredit,
      'Ending Balance': a.endingBalance
    }));
    exportToCSV(data, `Trial_Balance_${startDate}_to_${endDate}.csv`);
  };

  const handleTxLink = (accountId: string | null) => {
    if (onNavigate) {
      onNavigate('reports', 'Account Transactions', { accountId, startDate, endDate });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#141414]">
            {language === 'ar' ? 'ميزان المراجعة' : 'Trial Balance'}
          </h1>
          <p className="text-xs text-gray-500 font-medium">
            Financial state of your accounts between {startDate} and {endDate}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-[#141414]/10 px-3 py-1.5 rounded-lg shadow-sm">
            <Calendar className="w-3.5 h-3.5 opacity-40" />
            <div className="flex items-center gap-1">
              <input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-wider"
              />
              <span className="text-[10px] opacity-30">to</span>
              <input 
                type="date" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-wider"
              />
            </div>
          </div>
          <button 
            onClick={() => setIsClosingModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#141414] text-white rounded-lg text-xs font-bold hover:opacity-90 transition-opacity shadow-sm"
          >
            <Lock className="w-4 h-4" />
            Year-End Closing
          </button>
          <button 
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-[#00A09D] text-white rounded-lg text-xs font-bold hover:bg-[#00817e] transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="bg-white border-t-4 border-t-[#00A09D] shadow-xl rounded-t-lg overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50/50">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
            <input 
              type="text" 
              placeholder="Filter by name or code..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-gray-200 py-1.5 pl-10 pr-4 rounded-md text-xs outline-none focus:border-[#00A09D] transition-colors"
            />
          </div>

          <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
            {[
              { id: 'both' as const, label: 'Both' },
              { id: 'non-zero' as const, label: 'Non-Zero' },
              { id: 'zero' as const, label: 'Zero Only' }
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setVisibilityFilter(opt.id)}
                className={cn(
                  "px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all",
                  visibilityFilter === opt.id 
                    ? "bg-[#00A09D] text-white shadow-sm" 
                    : "text-gray-400 hover:text-gray-600"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-100/50 text-[10px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-200">
                <th className="p-4 w-24">Code</th>
                <th className="p-4 min-w-[200px]">Account</th>
                <th className="p-4 text-right w-32 border-l border-gray-200/50">Initial Balance</th>
                <th className="p-4 text-right w-32">Debit</th>
                <th className="p-4 text-right w-32">Credit</th>
                <th className="p-4 text-right w-32 border-l border-gray-200/50 bg-[#00A09D]/5 text-[#141414]">Ending Balance</th>
              </tr>
            </thead>
            <tbody className="text-xs">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-20 mb-2" />
                    <span className="opacity-40 italic serif">Auditing accounts...</span>
                  </td>
                </tr>
              ) : (
                <>
                  {categories.map(category => {
                    const accountsInCategory = filtered.filter(a => a.category === category);
                    if (accountsInCategory.length === 0) return null;

                    const catTotal = accountsInCategory.reduce((acc, a) => ({
                      initial: acc.initial + a.initialBalance,
                      debit: acc.debit + a.periodDebit,
                      credit: acc.credit + a.periodCredit,
                      ending: acc.ending + a.endingBalance
                    }), { initial: 0, debit: 0, credit: 0, ending: 0 });

                    return (
                      <React.Fragment key={category}>
                        <tr className="bg-gray-50/80 font-bold border-b border-gray-100">
                          <td colSpan={2} className="p-3 pl-4 text-[#00A09D] uppercase tracking-tight text-[11px]">
                            {category}
                          </td>
                          <td className="p-3 text-right font-mono border-l border-gray-200/50">
                            {formatCurrency(catTotal.initial)}
                          </td>
                          <td className="p-3 text-right font-mono">
                            {formatCurrency(catTotal.debit)}
                          </td>
                          <td className="p-3 text-right font-mono">
                            {formatCurrency(catTotal.credit)}
                          </td>
                          <td className="p-3 text-right font-mono bg-[#00A09D]/5">
                            {formatCurrency(catTotal.ending)}
                          </td>
                        </tr>
                        {[...accountsInCategory].sort((a,b) => a.code.localeCompare(b.code)).map((acc) => (
                          <tr key={acc.id} className="border-b border-gray-50 hover:bg-[#00A09D]/5 transition-colors group">
                            <td className="p-3 pl-8 font-mono opacity-50 tabular-nums">{acc.code}</td>
                            <td className="p-3">
                              <button 
                                onClick={() => handleTxLink(acc.id)}
                                className="text-left font-medium text-gray-700 hover:text-[#00A09D] hover:underline cursor-pointer"
                              >
                                {acc.name}
                              </button>
                            </td>
                            <td className="p-3 text-right font-mono text-gray-600 border-l border-gray-200/50 tabular-nums">
                              <button 
                                onClick={() => handleTxLink(acc.id)}
                                className="hover:text-[#00A09D] hover:underline cursor-pointer w-full text-right"
                              >
                                {formatCurrency(acc.initialBalance)}
                              </button>
                            </td>
                            <td className="p-3 text-right font-mono text-gray-600 tabular-nums">
                              {acc.periodDebit > 0 ? (
                                <button 
                                  onClick={() => handleTxLink(acc.id)}
                                  className="hover:text-[#00A09D] hover:underline cursor-pointer w-full text-right"
                                >
                                  {formatCurrency(acc.periodDebit)}
                                </button>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-right font-mono text-gray-600 tabular-nums">
                              {acc.periodCredit > 0 ? (
                                <button 
                                  onClick={() => handleTxLink(acc.id)}
                                  className="hover:text-[#00A09D] hover:underline cursor-pointer w-full text-right"
                                >
                                  {formatCurrency(acc.periodCredit)}
                                </button>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-right font-mono font-bold text-[#141414] border-l border-gray-200/50 bg-[#00A09D]/5 tabular-nums">
                              <button 
                                onClick={() => handleTxLink(acc.id)}
                                className="hover:text-[#00A09D] hover:underline cursor-pointer w-full text-right"
                              >
                                {formatCurrency(acc.endingBalance)}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* Grand Totals */}
                  <tr className="bg-[#141414] text-white font-bold h-12">
                    <td colSpan={2} className="p-4 text-right uppercase tracking-[0.2em] text-[10px] border-none">
                      Total
                    </td>
                    <td className="p-4 text-right font-mono text-sm border-l border-white/10">
                      {formatCurrency(totals.initial)}
                    </td>
                    <td className="p-4 text-right font-mono text-sm">
                      {formatCurrency(totals.debit)}
                    </td>
                    <td className="p-4 text-right font-mono text-sm">
                      {formatCurrency(totals.credit)}
                    </td>
                    <td className="p-4 text-right font-mono text-sm bg-white/10">
                      {formatCurrency(totals.ending)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer Legend */}
      <div className="flex items-center gap-6 mt-4 text-[10px] text-gray-400 font-medium px-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <span>Positive balance indicates Debit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
          <span>Negative balance indicates Credit</span>
        </div>
        <div className="ml-auto italic">
          Values are in base currency
        </div>
      </div>

      {/* Year-End Closing Modal */}
      {isClosingModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsClosingModalOpen(false);
          }}
        >
          <div className="bg-white w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#00A09D]" />
                Year-End Closing
              </h3>
              <button onClick={() => setIsClosingModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 opacity-40" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed font-medium">
                This process will transfer all balances from <span className="text-[#00A09D]">Income</span> and <span className="text-[#00A09D]">Expense</span> accounts to the <span className="text-[#00A09D]">Retained Earnings</span> account, effectively zeroing out the P&L for the new financial year.
              </p>
              
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Closing Date</label>
                <input 
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                  className="w-full bg-white border border-gray-200 p-3 rounded-xl text-sm font-bold outline-none focus:border-[#00A09D] transition-colors"
                />
              </div>

              <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
                <p className="text-xs text-amber-700 leading-normal">
                  <strong>Permanent Action:</strong> This will create a posted manual journal entry. Ensure all transactions for the year have been recorded before proceeding.
                </p>
              </div>
            </div>
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button 
                onClick={() => setIsClosingModalOpen(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={isClosing}
                onClick={handleYearEndClosing}
                className="flex-1 py-3 bg-[#141414] text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                {isClosing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Closing"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper for formatting
const formatCurrency = (val: number) => {
  const formatted = Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (val < 0) return `(${formatted})`;
  return formatted === '0.00' ? '-' : formatted;
};

// Removed local Loader2 definition as it is now imported from lucide-react or replaced by the one at the bottom if needed.
// Actually, lucide-react has Loader2.
